<script>
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useModalStore } from "@/stores/modal.js";
import { useAuthenticationStore } from "@/stores/authentication.js";
import { getUser } from '@/lib/api/users.js';

export default {
  setup() {
    // Stores
    const modalStore = useModalStore();
    const authenticationStore = useAuthenticationStore();

    // Variables
    const isLoading = ref(false);
    const username = ref(null);
    const password = ref(null);
    const showPassword = ref(false);

    // I18n
    const { t } = useI18n();

    onMounted(async () => { });

    const login = async () => {
      isLoading.value = true;

      try {
        const result = await authenticationStore.authenticateUser(username.value, password.value);
        if (result) {
          const user = await getUser(authenticationStore.currentUser.authenticatable_id)
          if (user) {
            authenticationStore.setUserCurrentUser(user.data);
            isLoading.value = false;
            username.value = null;
            password.value = null;
            modalStore.userEmail = null;
            modalStore.userVerificationPassword = null;
            modalStore.resetModal();
          }
        }
      } catch (error) {
        isLoading.value = false;

        if (error.message === 'You have to confirm your account before continuing.') {
          modalStore.userEmail = username.value;
          modalStore.userVerificationPassword = password.value;
          modalStore.setModal(modalStore.modalTypeConstants.RESEND_VERIFICATION)
        } else {
          modalStore.userEmail = null;
          modalStore.userVerificationPassword = null;
          modalStore.setPositiveHandleTrigger(false);
          modalStore.convertErrorsToMessages(error);
        }

        password.value = null;
      }
    }

    return {
      t,
      modalStore,
      isLoading,
      username,
      password,
      showPassword,
      login,
    }
  },
};
</script>

<template>
  <div class="justify-center items-center w-full">
    <form v-if="!isLoading" class="px-8 md:px-16 py-4 md:py-8" @submit="login">
      <div class="mb-4">
        <span class="font-bold text-xl">{{ t('modal.login.modal_body') }}</span>
      </div>
      <div class="mb-4">
        <label for="email" class="block text-gray-600 font-semibold">{{ t('modal.login.email_label') }}</label>
        <input type="email" id="email" name="email" v-model="username" class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300" :placeholder="t('modal.login.email_placeholder')">
      </div>
      <div class="mb-8">
        <div class="flex justify-between items-center">
          <label for="password" class="block text-gray-600 font-semibold">{{ t('modal.login.password_label') }}</label>
          <span class="block text-xs forgot-field cursor-pointer hover:opacity-75" @click="modalStore.setModal(modalStore.modalTypeConstants.FORGOT_PASSWORD)">{{ t('modal.login.forgot_password') }}</span>
        </div>
        <div class="relative">
          <input
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300"
            :placeholder="t('modal.login.password_placeholder')"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 top-1 right-0 px-3 flex items-center rounded-r-md focus:outline-none focus:outline-none focus:ring focus:ring-purple-300 hover:opacity-75"
          >
            <img v-if="showPassword" src="../../assets/icons/visibility_off_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1" class="text-white">
            <img v-else src="../../assets/icons/visibility_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1" class="text-white">
          </button>
        </div>
      </div>
      <div class="mb-4">
        <button type="submit" class="w-full text-white py-2 px-4 font-semibold rounded-full login-button hover:opacity-75" >{{ t('modal.login.login_button', { app_name: t('app_name') }) }}</button>
      </div>
      <div class="flex justify-center">
        <button class="block text-sm forgot-field hover:opacity-75" @click="modalStore.setModal(modalStore.modalTypeConstants.SIGN_UP)">{{ t('modal.login.dont_have_account') }}</button>
      </div>
    </form>
    <div v-else class="py-24 flex items-center justify-center">
      <div class="inline-block h-16 w-16 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/components/modals/login';
</style>